import { useEffect, useState, type JSX } from "react";
import type { ServerBrowserCity, ServerBrowserResponse } from "@stahlfront/shared";
import { fetchServerBrowser } from "../api.js";
import { useStore } from "../store.js";
import { SbLegend, SbServerSection, SbTopBar } from "./server-browser-bits.js";

type LoadState = "loading" | "ready" | "error";

export function ServerBrowserScreen(): JSX.Element {
  const [load, setLoad] = useState<LoadState>("loading");
  const [data, setData] = useState<ServerBrowserResponse | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [openServers, setOpenServers] = useState<Set<number>>(new Set([200]));
  const setSelectedCity = useStore((s) => s.setSelectedCity);
  const setCurrentScreen = useStore((s) => s.setCurrentScreen);

  useEffect(() => {
    let cancelled = false;
    setLoad("loading");
    setErrMsg(null);
    fetchServerBrowser()
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setLoad("ready");
        if (res.servers[0]) setOpenServers(new Set([res.servers[0].server.id]));
      })
      .catch((err) => {
        if (cancelled) return;
        setErrMsg(String(err));
        setLoad("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function toggleServer(id: number): void {
    setOpenServers((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function pickCity(city: ServerBrowserCity): void {
    setSelectedCity(city);
    setCurrentScreen("auth");
  }

  return (
    <div className="sb-screen">
      <SbTopBar />
      <main className="sb-main" aria-busy={load === "loading"}>
        <SbLegend />
        {load === "loading" && (
          <div className="sb-server-section sb-skeleton-section" aria-label="Loading servers">
            <div className="sb-server-header sb-skeleton-bar" />
            <div className="sb-city-grid">
              {Array.from({ length: 16 }, (_, i) => (
                <div key={i} className="sb-city-tile sb-skeleton-tile" data-skeleton="true" />
              ))}
            </div>
          </div>
        )}
        {load === "error" && (
          <div className="sb-error" role="alert">
            <p>Server list unavailable.</p>
            <p className="sb-error-detail">{errMsg}</p>
            <button
              type="button"
              className="sb-retry"
              onClick={() => {
                setLoad("loading");
                fetchServerBrowser()
                  .then((res) => {
                    setData(res);
                    setLoad("ready");
                  })
                  .catch((err) => {
                    setErrMsg(String(err));
                    setLoad("error");
                  });
              }}
            >
              Retry
            </button>
          </div>
        )}
        {load === "ready" &&
          data?.servers.map((entry) => (
            <SbServerSection
              key={entry.server.id}
              entry={entry}
              open={openServers.has(entry.server.id)}
              onToggle={() => toggleServer(entry.server.id)}
              onPickCity={pickCity}
            />
          ))}
      </main>
    </div>
  );
}
